import { inject, Injectable } from "@angular/core";
import {
  AccountMarketplace,
  CampaignType,
  Response,
  SbCreative,
  SbCreativeType,
  StrategyAsin,
  StrategyStateEnum,
  TacosStrategyGroup,
} from "@front/m19-api-client";
import { CAMPAIGN_TYPE, StrategyService } from "@front/m19-injection-token";
import {
  CampaignTypeWithTacos,
  getStrategyGroupState,
  sbAsinsToList,
  StrategyEx,
  StrategyGroupEx,
} from "@front/m19-models";
import {
  AccountSelectionService,
  BrandAssetsService,
  SbStrategyService,
  SpStrategyService,
  StrategyCache,
  TacosStrategiesService,
} from "@front/m19-services";
import {
  BulkImportService,
  TacosStrategyExport,
} from "@m19-board/strategies/strategy-bulk-upload-modal/bulk-import.service";
import { combineLatest, combineLatestWith, forkJoin, map, Observable, switchMap, take } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { StrategyBuilder } from "@front/m19-models/strategy/StrategyBuilder";
import { SpStrategyEx } from "@front/m19-models/strategy/SpStrategyEx";
import { SbStrategyEx } from "@front/m19-models/strategy/SbStrategyEx";
import { SdStrategyEx } from "@front/m19-models/strategy/SdStrategyEx";
import { DspStrategyEx } from "@front/m19-models/strategy/DspStrategyEx";

export interface StrategyLine<T extends StrategyEx> extends Partial<StrategyEx> {
  strategyId: number;
  strategyName: string;
  asins: StrategyAsin[];
  strategy: T;
  strategyGroupId?: number;
  strategyGroup?: StrategyGroupEx;
  isTacosStrategy?: boolean;
  isStrategyGroup?: boolean;
  hasSubStrategies?: boolean;
  campaignTypes?: CampaignType[];
  creatives?: SBStrategyLineCreative;
}

export type SBStrategyLineCreative = {
  [SbCreativeType.productCollection]: SbCreative[];
  [SbCreativeType.manualCollection]: SbCreative[];
  [SbCreativeType.video]: SbCreative[];
  [SbCreativeType.brandVideo]: SbCreative[];
  [SbCreativeType.storeSpotlight]: SbCreative[];
};

export interface StrategyGroupLine extends StrategyLine<SpStrategyEx> {
  strategyGroupId: number;
  isStrategyGroup: true;
  hasSubStrategies: boolean;
}

export interface SbStrategyLine extends StrategyLine<SbStrategyEx> {
  creatives: SBStrategyLineCreative;
}

export interface TacosStrategyLine extends StrategyLine<SpStrategyEx> {
  isTacosStrategy: true;
  campaignTypes: CampaignType[];
}

export type CampaignTypeStrategyLine = {
  [CampaignType.SP]: StrategyLine<SpStrategyEx>;
  [CampaignType.SD]: StrategyLine<SdStrategyEx>;
  [CampaignType.DSP]: StrategyLine<DspStrategyEx>;
  [CampaignType.SB]: SbStrategyLine;
  [CampaignTypeWithTacos.TACOS]: TacosStrategyLine;
};

@Injectable()
export class StrategyGridService<T extends CampaignTypeWithTacos> {
  private strategyService = inject(StrategyService);
  private readonly strategyCache = inject(StrategyCache);
  private tacosStrategiesService = inject(TacosStrategiesService);
  private accountSelectionService = inject(AccountSelectionService);
  private spStrategyService = inject(SpStrategyService);
  private bulkImportService = inject(BulkImportService);
  private sbStrategyService = inject(SbStrategyService);
  private readonly brandAssetsService = inject(BrandAssetsService);
  private readonly campaignType = inject<T>(CAMPAIGN_TYPE);

  accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$, { requireSync: true });

  private strategies$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.strategyService.getStrategies(am.accountId, am.marketplace)),
    map((strategies) => Array.from(strategies.values())),
  );

  private allSpStrategiesIndex$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.strategyCache.getStrategyIndex(am.accountId, am.marketplace, CampaignType.SP)),
  );

  private sbStrategies$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => {
      return combineLatest([
        this.strategies$,
        this.brandAssetsService.getSbCreativesPerStrategy(am.accountId, am.marketplace),
      ]);
    }),
    map(([sbStrats, sbCreatives]) => {
      return sbStrats
        .filter((x) => !x.isTacos())
        .filter((x) => x.isSbStrategy())
        .map((s) => this.convertSbStrategyToLine(s, sbCreatives.get(s.strategyId) ?? []));
    }),
  );

  private strategyGroups$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.spStrategyService.getStrategyGroups(am.accountId, am.marketplace)),
    map((strategyGroups) => Array.from(strategyGroups.values()).filter((x) => (x.strategies?.length ?? 0) > 0)),
  );

  private tacosStrategies$ = this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.tacosStrategiesService.getTacosStrategyIndex(am.accountId, am.marketplace)),
    map((tacosStrategies) => Array.from(tacosStrategies.values())),
  );

  private tacosStrategiesToDisplay$: Observable<TacosStrategyLine[]> = combineLatest([
    this.tacosStrategies$,
    this.allSpStrategiesIndex$,
  ]).pipe(
    map(([tacosStrategies, allSpStrategies]) => {
      return tacosStrategies.map((sg) => this.convertTacosStrategyToLine(sg, allSpStrategies));
    }),
    map((tacosStrategyLines) => tacosStrategyLines.filter((ts): ts is TacosStrategyLine => !!ts)),
  );

  private filteredStrategies$ = this.strategies$.pipe(
    map((strategies) => strategies.filter((x) => x.isLegacy() && !x.isTacos())),
    map((strategies) => strategies.map((s) => this.convertStrategyToLine(s))),
  );

  private spStrategiesAndStrategyGroups$: Observable<StrategyLine<SpStrategyEx>[]> = this.filteredStrategies$.pipe(
    combineLatestWith(this.tacosStrategies$),
    map(([strategies, tacosStrategies]) =>
      (strategies as StrategyLine<SpStrategyEx>[]).filter((s) => {
        // hide SP strategies that are used in Tacos strategies
        return !tacosStrategies.some((t) => t.spStrategyId === s.strategyId!);
      }),
    ),
    combineLatestWith(this.strategyGroups$),
    map(([strategies, strategyGroups]) => {
      const strategyGroupLines: StrategyLine<SpStrategyEx>[] = strategyGroups.map((sg) => {
        return this.convertStrategyGroupToLine(sg);
      });

      return [...strategies, ...strategyGroupLines].sort((a, b) => this.compareStrategies<StrategyEx>(a, b));
    }),
  );

  private otherStrategies$ = this.filteredStrategies$;

  readonly datasource$ = this.getDataSource();

  readonly tacosStrategiesToExport$: Observable<TacosStrategyExport[]> = this.tacosStrategiesToDisplay$.pipe(
    map((tacos: TacosStrategyLine[]) => {
      const toExport = [];
      for (const ts of tacos.values()) {
        toExport.push({
          strategyId: ts.strategyId,
          name: ts.strategy!.name!,
          campaignType: ts.strategy!.campaignType,
          algoMode: ts.strategy!.algoMode,
          asins: ts.strategy?.asins?.map((a) => a.asin!) ?? [],
          tacosStrategyGroupId: ts.strategyGroupId,
          tacosTarget: ts.strategy.tacosTarget!,
          state: ts.state!,
          campaignTypes: ts.campaignTypes.filter((c) => c !== CampaignType.SP),
        });
      }
      return toExport;
    }),
  );

  deleteStrategy(strategy: StrategyLine<StrategyEx>, organizationId: number): Observable<Response> {
    if (strategy.isStrategyGroup) {
      return this.spStrategyService.deleteStrategyGroup(
        strategy.accountId!,
        strategy.marketplace!,
        organizationId,
        strategy.strategyGroupId!,
      );
    } else if (strategy.isTacosStrategy) {
      return this.tacosStrategiesService.deleteTacosStrategyGroup(
        strategy.accountId!,
        strategy.marketplace!,
        organizationId,
        strategy as unknown as TacosStrategyGroup,
      );
    }

    return this.strategyService.deleteStrategy(strategy.strategy, organizationId);
  }

  deleteStrategies(strategies: StrategyLine<StrategyEx>[], organizationId: number): Observable<Response[]> {
    return forkJoin(strategies.map((strategy) => this.deleteStrategy(strategy, organizationId)));
  }

  exportTacosStrategies(accountMarketplace: AccountMarketplace) {
    this.tacosStrategiesToExport$.pipe(take(1)).subscribe((strategies) => {
      this.bulkImportService.exportTacosStrategyCsv(accountMarketplace, strategies);
    });
  }

  exportStrategies(campaignType: CampaignType, accountMarketplace: AccountMarketplace) {
    const source = this.getExportableStrategiesFromCampaignType(campaignType);
    source.pipe(take(1)).subscribe((strategies) => {
      this.bulkImportService.exportStrategyCsv(accountMarketplace, campaignType, strategies);
    });
  }

  private getExportableStrategiesFromCampaignType(campaignType: CampaignType): Observable<StrategyEx[]> {
    if (campaignType === CampaignType.SP) {
      return combineLatest([this.spStrategiesAndStrategyGroups$, this.strategyGroups$]).pipe(
        take(1),
        map(([strategies, strategyGroups]) => {
          const productSubStrategies = strategyGroups
            .flatMap((sg) => sg.productStrategies)
            .filter((s) => !strategies.some((st) => st.strategyId === s.strategyId));

          return strategies
            .map((s) => s.strategy)
            .concat(productSubStrategies)
            .concat(strategyGroups.flatMap((sg) => sg.brandStrategies))
            .concat(strategyGroups.flatMap((sg) => sg.keywordStrategies));
        }),
      );
    }

    return this.datasource$.pipe(
      map((strategies) => strategies.map((s) => s.strategy)),
      take(1),
    );
  }

  private sortStrategies(strategies: CampaignTypeStrategyLine[T][]) {
    return strategies.sort((a, b) => this.compareStrategies<StrategyEx>(a, b));
  }

  private getDataSource(): Observable<CampaignTypeStrategyLine[T][]> {
    const dataFetchers: { [K in CampaignTypeWithTacos]: () => Observable<CampaignTypeStrategyLine[K][]> } = {
      [CampaignType.SP]: () => this.spStrategiesAndStrategyGroups$,
      [CampaignType.SB]: () => this.sbStrategies$,
      [CampaignType.SD]: () => this.otherStrategies$ as Observable<StrategyLine<SdStrategyEx>[]>,
      [CampaignType.DSP]: () => this.otherStrategies$,
      [CampaignTypeWithTacos.TACOS]: () => this.tacosStrategiesToDisplay$,
    };

    return dataFetchers[this.campaignType]().pipe(map((strategies) => this.sortStrategies(strategies)));
  }

  private convertTacosStrategyToLine(
    sg: TacosStrategyGroup,
    spStrategies: Map<number, SpStrategyEx>,
  ): TacosStrategyLine | undefined {
    const spStrategy = spStrategies.get(sg.spStrategyId!)!;
    const campaignTypes: CampaignType[] = [CampaignType.SP];

    if (!spStrategy) return undefined;

    if (sg.sdStrategyId && sg.sdSpendRepartition) {
      campaignTypes.push(CampaignType.SD);
    }
    if (sg.sbStrategyId && sg.sbSpendRepartition) {
      campaignTypes.push(CampaignType.SB);
    }

    return {
      ...sg,
      state: sg.state!,
      strategyId: sg.spStrategyId!,
      strategyName: spStrategy.name ?? "",
      asins: spStrategy.asins ?? [],
      strategy: StrategyBuilder.createStrategy<CampaignType.SP>({ ...spStrategy }).withTacosTarget(sg.tacosTarget!),
      strategyGroupId: sg.tacosStrategyGroupId,
      campaignTypes: campaignTypes,
      isTacosStrategy: true,
    };
  }

  private convertStrategyGroupToLine(sg: StrategyGroupEx): StrategyGroupLine {
    const firstStrategy = sg.strategies![0]!;

    const strategyName = sg.strategies?.length == 1 ? firstStrategy.name : sg.strategyGroupName;

    return {
      ...sg,
      strategyId: firstStrategy.strategyId!,
      asins: sg.asins.map((asin) => ({ asin })),
      state: getStrategyGroupState(sg),
      strategyName: strategyName ?? "",
      algoMode: firstStrategy.algoMode,
      isStrategyGroup: true,
      hasSubStrategies: sg.strategies!.length > 1,
      strategy: StrategyBuilder.createStrategy<CampaignType.SP>(firstStrategy),
      strategyGroupId: sg.strategyGroupId!,
      strategyGroup: sg,
    };
  }

  private convertSbStrategyToLine(s: SbStrategyEx, creatives: SbCreative[]): SbStrategyLine {
    const strategyLineCreatives: SBStrategyLineCreative = {
      [SbCreativeType.video]: [],
      [SbCreativeType.productCollection]: [],
      [SbCreativeType.manualCollection]: [],
      [SbCreativeType.storeSpotlight]: [],
      [SbCreativeType.brandVideo]: [],
    };

    const asins: Set<string> = new Set();
    for (const creative of creatives) {
      strategyLineCreatives[creative.creativeType].push(creative);
      for (const creativeAsins of creative.creativeAsins!) {
        for (const asin of sbAsinsToList(creativeAsins)) {
          asins.add(asin);
        }
      }
    }

    return {
      ...this.convertStrategyToLine<SbStrategyEx>(s),
      asins: Array.from(asins).map((asin) => ({ asin })),
      creatives: strategyLineCreatives,
    };
  }

  private convertStrategyToLine<T extends StrategyEx>(s: T): StrategyLine<T> {
    return {
      ...s,
      strategy: s,
      strategyName: s.name,
    };
  }

  private compareStrategies<T extends StrategyEx>(a: StrategyLine<T>, b: StrategyLine<T>) {
    if (a.state === b.state) {
      if (b.defaultStrategy) {
        return -1;
      }
      if (a.defaultStrategy) {
        return 1;
      }
      return a.strategyName.localeCompare(b.strategyName);
    }
    return a.state === StrategyStateEnum.ENABLED ? -1 : 1;
  }
}
